import React from 'react'
import { FieldArray, useFormikContext } from 'formik'
import { Element, Heading } from 'react-bulma-components'
import { WorkersRequest } from '../../../../../api/workers-requests'
import { Employer } from '../../../../../api/organizations'
import { useCreatePost, useUpdatePost } from '../../../../../queries/workers-requests'
import PostCard from '../post-card/post-card'
import {
  WorkersRequestCreateUpdateFormValues,
  WorkersRequestFormErrors,
} from '../workers-request-form.types'

/**
 * Props of the `<PostsSection>` component.
 */
export interface PostsSectionProps {
  workersRequest?: WorkersRequest
  employer: Employer
  initialValues: WorkersRequestCreateUpdateFormValues
  isSubmitDisabled?: boolean
  // Mutation instances owned by the top-level form. Forwarded to each `PostCard`
  // so the feedback UI (`RequestMessage` / `RequestButton`) stays bound to the
  // same instance the submit flow is actually calling.
  updatePostMutation: ReturnType<typeof useUpdatePost>
  createPostMutation: ReturnType<typeof useCreatePost>
}

/**
 * "Postes à Pourvoir" section: renders the list of post cards via a FieldArray.
 *
 * Each card owns its own mutations (createPost / updatePost / deletePost) and its
 * "updated vs initial" diff. The FieldArray layer is kept thin on purpose.
 */
const PostsSection: React.FC<PostsSectionProps> = ({
  workersRequest,
  employer,
  initialValues,
  isSubmitDisabled,
  updatePostMutation,
  createPostMutation,
}) => {
  const { values, errors } = useFormikContext<WorkersRequestCreateUpdateFormValues>()
  const formErrors = errors as WorkersRequestFormErrors

  return (
    <>
      <Heading size={4} mt={6}>
        Postes à Pourvoir
      </Heading>
      {formErrors.posts && typeof formErrors.posts === 'string' && (
        <Element textColor="danger">{formErrors.posts}</Element>
      )}
      <FieldArray
        name="posts"
        render={arrayHelpers => (
          <>
            {values.posts.map((post, index, allPosts) => {
              const isExistingPost = '_id' in post
              const key = isExistingPost ? post._id : index
              // Initial post values (if any) used by PostCard to compute the update-diff.
              const initialPost = isExistingPost
                ? initialValues.posts.find(p => '_id' in p && p._id === post._id)
                : undefined

              return (
                <PostCard
                  key={key}
                  index={index}
                  post={post}
                  allPostsCount={allPosts.length}
                  isLast={index === allPosts.length - 1}
                  arrayHelpers={arrayHelpers}
                  workersRequest={workersRequest}
                  employer={employer}
                  initialPost={initialPost}
                  isSubmitDisabled={isSubmitDisabled}
                  updatePostMutation={updatePostMutation}
                  createPostMutation={createPostMutation}
                />
              )
            })}
          </>
        )}
      />
    </>
  )
}

export default PostsSection
